import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppHistoryDone, AppSendHistory } from "./AppSendHistory";

// Where the app reads the folder from. TWO adapters behind ONE shape: `AppLocalSource` (below) and
// `AppDropboxSource` (the subscriber's own Dropbox, and `/send`).
//
// ⚠ THE LOCAL ADAPTER IS NOT A TEST DOUBLE. `file://` against the generated folder is how this app
// is developed and how it is verified in a browser - both adapters are real; neither is the "mock".
//
// ⚠ `AppDriveSource` WAS DELETED (2026-09-24). Google sign-in had been removed from `AppMain`, so
// nothing could construct it, and KBuild kept all ~400 lines of it in the bundle anyway. Its
// `FolderName` ("WA Commander") was the NAME of a real Google Drive folder the old bridge wrote -
// `DriveSyncRun.RootName` - so it was never display text to correct; it went with the class.
//
// ⚠ NO `fetch`, NO PROMISES. House law (`coding-typescript`): callbacks only. XHR takes them
// natively, and it is also the only one of the two that works over `file://` in some browsers -
// `fetch` rejects a `file:` URL outright.
//
// ⚠ WHY NOT `kSPRest.RestExecute` (law 2, and the reinvention guard asks every time). It is the
// SharePoint transport, not a general one: it queues into `kSPRest._RestWaitingLine` and hands the
// request to `RestQuery.Execute()`, which resolves the web url, attaches `X-RequestDigest`, and
// branches on `ks.KApplication.apptype` to route an Add-in through the cross-domain executor. This
// app is a static page talking to Dropbox, to our own service and to `file://`. There is no
// SharePoint web, no digest, no page context - so the shared transport has nothing to do here and
// would need every one of those pieces stubbed to be silent. Checked before writing: the shared
// surface has no general HTTP helper, only the two SharePoint-bound ones.

export type AppTextDone = (Text: string) => void;

/**
 * How a queued message has fared. `Final` is true when there is nothing more to wait for - sent,
 * refused for a reason that will not change by asking again, or `Unconfirmed`: WhatsApp never
 * answered, so it may or may not have gone and the sender will not try again (`Why` says to check
 * the phone). ⛔ Unconfirmed is never "sent" and never a failure - both invite a second copy.
 */
export type AppSendState = (Sent: boolean, Final: boolean, Why: string, Unconfirmed: boolean, Extra?: AppSendExtra) => void;

/**
 * ⭐ TRANSLATION's additions to a send's verdict (2026-09-27): `SentText` - the words that WENT OUT when they differ
 * from what the owner typed ("" otherwise); `TranslateFailed` - the words could not be translated, so NOTHING was
 * sent (the bubble offers Retry / Send in English). A source that cannot tell passes none.
 */
export class AppSendExtra {
    public SentText: string = "";
    public TranslateFailed: boolean = false;
}

/** A failure the USER can act on - offline, signed out, folder not shared. Distinct from a defect,
 *  which goes to `KGlobalErrorHandler` and never reaches this callback. */
export type AppTextFail = (Why: string) => void;

export class AppSource {
    /** Hosted sources use tenant-authorized FTS; local folders keep their offline search file. */
    public CanQueryMessages = (): boolean => { return false; };
    public QueryMessages = (Query: Record<string, unknown>, Done: AppTextDone): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { Done('{"ok":false,"why":"Search is unavailable."}'); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSource.QueryMessages"); }
    };
    /** `index.json` - the one request the home screen costs. */
    public LoadIndex = (OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            OnFail("No source configured.");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSource.LoadIndex");
        }
    };

    /** One contact page, by the `href` the index gave. */
    public LoadPage = (Href: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            OnFail("No source configured for " + Href + ".");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSource.LoadPage");
        }
    };

    /**
     * One photo or voice note beside a page, answered with a URL an element inside the contact
     * frame can load (`AppMediaFill`). `Href` is folder-relative (`AppMediaRules.Resolve`).
     *
     * ⚠ A FAILURE IS FATE 1 AND ANSWERS `OnFail`: a file not synced yet or an expired ticket is a
     * normal condition, and the page keeps its "Photo" alt text rather than blanking the archive.
     */
    public DownloadMedia = (ChatId: string, MessageId: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { OnFail("On-demand media is available when signed in to the hosted service."); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSource.DownloadMedia"); }
    };

    public LoadMedia = (Href: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            OnFail("No source configured for " + Href + ".");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSource.LoadMedia");
        }
    };

    /**
     * A person file beside a page (`People/X/ai-chat.json`), by its folder-relative path: `OnDone` with its
     * text, `OnMissing` when there is none (Dropbox's 409 `path/not_found`), `OnFail` with a sentence.
     * ⚠ FATE 1 on every failure. ⚠ This base (and the local copy) has no service, so no AI history: missing.
     */
    public LoadBeside = (Href: string, OnDone: AppTextDone, OnMissing: () => void, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            OnMissing();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSource.LoadBeside");
        }
    };

    /**
     * ⛔ THE ONE THING THE APP WRITES. A send request into `Outbox/`, and nothing else, ever.
     *
     * ⚠ IT DOES NOT BREAK THE ONE-WRITER RULE, AND THE REASON IS THE NAMESPACE. Design rule 5 says
     * the service owns every file in the folder, so the app cannot tell a human edit from a
     * generated one. That rule is about the GENERATED files - the pages, the index, the manifest -
     * and this touches none of them. `Outbox/<id>.json` is written by the app and never by the
     * bridge; `Outbox/<id>.receipt.json` is written by the bridge and never by the app. The two
     * suffixes cannot collide, so there is no file with two owners and nothing to merge.
     */
    public Queue = (Name: string, Json: string, OnDone: AppTextDone, OnFail: AppTextFail, OnUnknown: AppTextDone | null = null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            OnFail("No source configured for " + Name + ".");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSource.Queue");
        }
    };

    /**
     * Can this source say when a queued message has actually been SENT?
     *
     * ⚠ FALSE HERE, and the page then keeps its older, honest "queued" wording. Only a source that
     * can ask the sender - `AppDropboxSource`, through `/send` - may promise "Sent".
     *
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public CanCheck = (): boolean => {
        return false;
    };

    /** Ask how a queued message has fared. A source that cannot tell never calls back as sent. */
    public Check = (Id: string, OnState: AppSendState): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            OnState(false, true, "", false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSource.Check");
        }
    };

    /**
     * Can this source list the messages already sent? ⚠ Only one that can ask the sender.
     *
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public CanHistory = (): boolean => {
        return false;
    };

    /** The messages already sent, newest first. A source that cannot tell answers with a sentence. */
    public History = (OnDone: AppHistoryDone): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            OnDone(AppSendHistory.No("Sent messages can only be listed on the hosted WACommander."));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSource.History");
        }
    };

    /**
     * The Send button's tooltip (and the box's screen-reader description): who sends, and when.
     *
     * ⚠ THIS ONE IS FOR THE OWNER'S OWN PC (`npm run serve`), where it is true: nothing leaves
     * until `live` is running. A hosted page must not say it - see `AppDropboxSource.SendNote`.
     *
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public SendNote = (): string => {
        return "Sent by WACommander on this computer. Nothing leaves while `npm run live` is not"
            + " running.";
    };

    /**
     * Who is approving a send, for `approvedBy`. "" means nobody is - and the bridge refuses a
     * request with an empty one, which is decision 9 working rather than failing.
     *
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public Who = (): string => {
        return "";
    };

    /**
     * What the UI calls this source, for the footer.
     *
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public Describe = (): string => {
        return "none";
    };
}

/**
 * The folder as files, relative to wherever the app is served from.
 *
 * Used for `file://` development, and for `npm run serve` on the owner's own PC.
 */
export class AppLocalSource extends AppSource {
    private Root: string;

    public constructor(Root: string) {
        super();
        this.Root = Root;
    }

    public LoadIndex = (OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Get(this.Root + "index.json", OnDone, OnFail);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLocalSource.LoadIndex");
        }
    };

    public LoadPage = (Href: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Get(this.Root + Href, OnDone, OnFail);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLocalSource.LoadPage");
        }
    };

    /**
     * ⚠ AN ABSOLUTE URL, BECAUSE THE ELEMENT LIVES IN A `blob:` FRAME: a relative one would resolve
     * against the frame's `blob:` address, which cannot be a base, and load nothing. Resolved against
     * THIS page, it is the file on the local server (or on disk) - no request is needed here.
     * ⚠ `kSPListCore.PathOf` (law 2) reads a path OUT of a URL; this builds an absolute URL FROM a
     * relative one against an explicit base, which is `new URL`'s two-argument form and cannot throw
     * on a relative value the way the one-argument form does.
     */
    public LoadMedia = (Href: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            OnDone(new URL(this.Root + Href, window.location.href).href);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLocalSource.LoadMedia");
        }
    };

    /**
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public Describe = (): string => {
        return "this folder";
    };

    /**
     * ⚠ IT POSTS, AND THAT IS WHY `file://` CANNOT SEND. There is nothing to POST to when the page
     * was opened from disk, and a browser will not write a file. `npm run serve` is what makes this
     * mode complete, and it binds 127.0.0.1 only - so "the human at the keyboard" is a truthful
     * approver here in a way it would not be on a shared address.
     */
    public Queue = (Name: string, Json: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Req: XMLHttpRequest = new XMLHttpRequest();
            Req.onreadystatechange = new AppLocalReply(Req, "", OnDone, OnFail).OnPosted;
            Req.open("POST", this.Root + "outbox/" + encodeURIComponent(Name), true);
            Req.setRequestHeader("Content-Type", "application/json");
            Req.send(Json);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLocalSource.Queue");
        }
    };

    /**
     * ⚠ NOT A PLACEHOLDER. `npm run serve` binds 127.0.0.1, so the only person who can reach this
     * page IS the owner at the keyboard - which is exactly what `approvedBy` is asserting.
     *
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public Who = (): string => {
        return "local (127.0.0.1)";
    };

    private Get = (Url: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Req: XMLHttpRequest = new XMLHttpRequest();
            Req.onreadystatechange = new AppLocalReply(Req, Url, OnDone, OnFail).OnRead;
            Req.open("GET", Url, true);
            Req.send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLocalSource.Get");
        }
    };
}

/**
 * Reads one local XHR answer. A named class rather than the inline closures it replaces - house
 * rule, and the closures could not be reached by a test.
 *
 * ⚠ AN ANSWER IS DELIVERED ONCE (`Ended`): `readystatechange` can fire at 4 more than once in some
 * engines, and two answers to one read would paint a page twice.
 */
export class AppLocalReply {
    private Req: XMLHttpRequest;
    private Url: string;
    private Done: AppTextDone;
    private Fail: AppTextFail;
    private Ended: boolean = false;

    public constructor(Req: XMLHttpRequest, Url: string, Done: AppTextDone, Fail: AppTextFail) {
        this.Req = Req;
        this.Url = Url;
        this.Done = Done;
        this.Fail = Fail;
    }

    /**
     * ⚠ `status === 0` IS SUCCESS OVER `file://`. A local XHR reports no HTTP status at all,
     * because there was no HTTP. Treating 0 as failure - the obvious reading - makes the app report
     * "could not load" for every file it just read perfectly.
     */
    public OnRead = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Req.readyState !== 4 || this.Ended === true) { return; }
            this.Ended = true;
            const Ok: boolean = this.Req.status === 0 && this.Req.responseText.length > 0;
            if (Ok === true || (this.Req.status >= 200 && this.Req.status < 300)) {
                this.Done(this.Req.responseText);
                return;
            }
            this.Fail("Could not read " + this.Url + " (status " + String(this.Req.status) + ").");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLocalReply.OnRead");
        }
    };

    /** ⚠ Here status 0 is NOT success: a POST with no server behind it has gone nowhere. */
    public OnPosted = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Req.readyState !== 4 || this.Ended === true) { return; }
            this.Ended = true;
            if (this.Req.status >= 200 && this.Req.status < 300) { this.Done(this.Req.responseText); return; }
            if (this.Req.status === 0) {
                this.Fail("Nothing is listening. Sending needs `npm run serve` - a page opened "
                    + "straight from the folder has nowhere to put the request.");
                return;
            }
            this.Fail("The local server returned " + String(this.Req.status) + ".");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLocalReply.OnPosted");
        }
    };
}
